<template>
  <div
    class="desktop-header"
    :class="{
      'with-border': showBorder,
      'with-shadow': showShadow,
      'with-navigation': $slots.navigation,
    }"
  >
    <div class="header-content">
      <!-- Navigation Button -->
      <div v-if="$slots.navigation" class="header-navigation">
        <slot name="navigation" />
      </div>

      <!-- Title Section -->
      <div class="title-section">
        <h1 class="page-title">
          <IonIcon v-if="icon" :icon="icon" class="title-icon" />
          {{ title }}
        </h1>
        <p v-if="subtitle" class="page-subtitle">{{ subtitle }}</p>
      </div>

      <!-- Actions Section -->
      <div v-if="$slots.actions" class="header-actions">
        <slot name="actions" />
      </div>
    </div>

    <!-- Secondary Content (breadcrumbs, filters, etc.) -->
    <div v-if="$slots.secondary" class="header-secondary">
      <slot name="secondary" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { IonIcon } from '@ionic/vue';

interface Props {
  title: string;
  subtitle?: string;
  icon?: string;
  showBorder?: boolean;
  showShadow?: boolean;
}

defineProps<Props>();
</script>

<style scoped>
.desktop-header {
  width: 100%;
  background: var(--ion-background-color);
  padding: 1.5rem 5rem 1rem 2rem;
  position: sticky;
  top: 0;
  z-index: 30;
}

.desktop-header.with-border {
  border-bottom: 1px solid var(--ion-input-border-color);
}

.desktop-header.with-shadow {
  box-shadow: var(--ion-box-shadow);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
}

.title-section {
  min-width: 0;
}

.page-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--ion-text-color-primary);
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.title-icon {
  font-size: 1.5rem;
  color: var(--ion-color-primary);
  flex-shrink: 0;
}

.page-subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: var(--ion-text-color-secondary);
  font-weight: 400;
  line-height: 1.4;
}

.header-navigation {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.with-navigation .header-content {
  align-items: center;
}

.with-navigation .title-section {
  flex: 1;
  text-align: right;
}

.with-navigation .page-title {
  justify-content: flex-end;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
  min-width: 0;
}

.header-secondary {
  border-top: 1px solid var(--ion-card-border-color);
  padding-top: 1rem;
  margin-top: 0.5rem;
}
</style>
